import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import CommonLayout from "../Common/Layout";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";

import ViewJob from "./ViewJob";
import AssignJob from "./AssignJob";
import Sample from "./Sample";

import JobService from "../../service/job";
import Order from "./Order";
import Artwork from "./Artwork";
import { useSelector } from "react-redux";
import Addon from "./Addon";

const statuses = {
  1: "Awaiting Assign",
  2: "Awaiting Order",
  3: "Awaiting Order Approval",
  4: "Artwork in process",
  5: "Awaiting Artwork Approval",
  6: "Image File in process",
  7: "Awaiting Image File Approval",
  8: "In Production",
  9: "Delivery Underway",
  10: "Completed",
};

const TabValues = {
  0: "ViewJob",
  1: "AssignJob",
  2: "Order",
  3: "Artwork",
  4: "ImageFile",
  5: "Addon",
};

const pageHash = {
  assign: 1,
  order: 2,
  artwork: 3,
  imagefile: 4,
  updates: 5,
};

function TabPanel(props) {
  const { children, value, role, name, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={TabValues[value] !== name}
      id={`simple-tabpanel-${name}`}
      aria-labelledby={`simple-tab-${name}`}
      {...other}
    >
      {TabValues[value] === name && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

const Job = () => {
  const { job_id } = useParams();
  const { user } = useSelector((state) => state);
  const role = user.currentUser?.Role_id;

  const [value, setValue] = useState(0);

  const [loading, setLoading] = useState(true);
  const [jobName, setJobName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [productLink, setProductLink] = useState("");
  const [categoryProductLink, setCategoryProductLink] = useState(1);
  const [images, setImages] = useState([]);
  const [DeliveryFormats, setDeliveryFormats] = useState([]);
  const [employee, setEmployee] = useState("");
  const [employeeDescription, setEmployeeDescription] = useState([]);
  const [amount, setAmount] = useState("");
  const [orderDescription, setOrderDescription] = useState("");
  const [orderComments, setOrderComments] = useState("");
  const [artworkFiles, setArtworkFiles] = useState([]);
  const [artworkDescription, setArtworkDescription] = useState("");
  const [artworkComments, setArtworkComments] = useState("");
  const [sampleFiles, setSampleFiles] = useState([]);
  const [sampleDescription, setSampleDescription] = useState("");
  const [addons, setAddons] = useState([]);
  const [categoryImageFileStep, setCategoryImageFileStep] = useState(1);
  const [supplier, setSupplier] = useState(null);

  const [tabPanelHeight, setTabPanelHeight] = useState(0);
  const { hash } = useLocation();

  const isSupplierJob = useMemo(() => !!supplier, [supplier]);

  // Get job status text based on status id
  const JobDetailsSpan = () => {
    return (
      <>
        {"Job View - " + job_id + " "}
        <span
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            color: status != 10 ? "#FD9A13" : "green",
          }}
        >
          {status != "" ? "( " + statuses[status] + " )" : ""}
        </span>
      </>
    );
  };

  useLayoutEffect(() => {
    function updateSize() {
      setTabPanelHeight(window.innerHeight - 150);
    }
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    const Job = new JobService();

    Job.getJob(job_id).then((response) => {
      if (response.success) {
        const job = response.data;

        setImages(job.Images.map(({ image }) => image));
        setJobName(job.Name);
        setJobDescription(job.Description);
        setCategory(job.CategoryName);
        setSubcategory(job.SubcategoryName);
        setProductLink(job.ProductLink);
        setCategoryProductLink(job.CategoryProductLink);
        setDeliveryFormats(job.DeliveryFormats);
        setAmount(job.Amount || "");
        setOrderDescription(job.QuotationDescription || "");
        setOrderComments(job.QuotationComments || "");
        setEmployee(job.Employee_id || "");
        setEmployeeDescription(job.EmployeeDescription || "");
        setArtworkFiles(job.Artworks.map(({ image }) => image));
        setArtworkDescription(job.ArtworkDescription || "");
        setArtworkComments(job.ArtworkComments || "");
        setSampleFiles(job.Samples.map(({ image }) => image));
        setSampleDescription(job.SampleDescription || "");
        setStatus(job.Status);
        setAddons(job.Addons);
        setCategoryImageFileStep(job.CategoryImageFileStep);
        setSupplier(job.Supplier_id);

        if (hash && pageHash[hash.replace("#", "")]) setValue(parseInt(pageHash[hash.replace("#", "")]));

        setLoading(false);
      }
    });
  }, [job_id]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const breadcrumbArr = [
    {
      link: user.currentUser && (user.currentUser.isAdminOrEmp === 1 || role === 4) ? "/admin/jobs" : "/jobs",
      displayName: "Jobs",
      active: false,
    },
    { link: "#", displayName: "Job View", active: true },
  ];

  const disableArtwork = useMemo(() => {
    if (role === 1) return status >= (isSupplierJob ? 5 : 4);
    if (role === 3) return status == 4 || status == 5;
    if (role === 2) return status >= (isSupplierJob ? 5 : 4);
    if (role === 4) return status == 4 || status == 5;
    return false;
  }, [isSupplierJob, role, status]);

  return (
    <CommonLayout title={<JobDetailsSpan />} breadcrumbArr={breadcrumbArr}>
      <Container
        maxWidth="false"
        sx={{ m: 0 }}
        style={{
          overflow: "hidden",
          height: "100%",
          padding: 0,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "70vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", pl: 3, pr: 3 }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="View Job" />
                <Tab label="Assign" style={{ display: role === 3 || isSupplierJob ? "none" : "" }} />
                <Tab
                  label="Order"
                  {...{
                    ...(((role === 1 || role === 2) && status >= 2) || ((role === 3 || role === 4) && status == 3)
                      ? {}
                      : { disabled: true }),
                  }}
                />
                <Tab
                  label="Artwork"
                  {...{
                    ...(disableArtwork ? {} : { disabled: true }),
                  }}
                />
                <Tab
                  label="Image File"
                  style={{ display: categoryImageFileStep == 1 ? "" : "none" }}
                  {...{
                    ...(status <= 5 ||
                      (role === 3 && (status == 6 || status >= 8)) ||
                      (isSupplierJob && (role === 2 || role === 1) && status < 7) ||
                      (role === 4 && (status != 6 && status != 7))
                      ? { disabled: true }
                      : {}),
                  }}
                />
                <Tab label="Order Updates" />
              </Tabs>
            </Box>
            <TabPanel
              value={value}
              name="ViewJob"
              style={{
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              <ViewJob
                {...{
                  job_id,
                  jobName,
                  jobDescription,
                  category,
                  subcategory,
                  productLink,
                  categoryProductLink,
                  images,
                  DeliveryFormats,
                  setValue,
                  role,
                  status,
                  amount,
                  orderDescription,
                }}
              />
            </TabPanel>
            {role !== 3 && !isSupplierJob && (
              <TabPanel
                value={value}
                name="AssignJob"
                style={{
                  paddingLeft: "24px",
                  paddingRight: "24px",
                }}
              >
                <AssignJob
                  readOnly={role === 2}
                  job_id={job_id}
                  defaultEmployee={employee}
                  defaultEmployeeDescription={employeeDescription}
                  setStatus={setStatus}
                  setValue={setValue}
                />
              </TabPanel>
            )}
            <TabPanel
              value={value}
              name="Order"
              style={{
                overflowY: "auto",
                height: `${tabPanelHeight}px`,
                maxHeight: `${tabPanelHeight}px`,
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              <Order
                setStatus={setStatus}
                job_id={job_id}
                amount={amount}
                orderDescription={orderDescription}
                orderComments={orderComments}
                role={role}
                setValue={setValue}
              />
            </TabPanel>
            <TabPanel
              value={value}
              name="Artwork"
              style={{
                overflowY: "auto",
                height: `${tabPanelHeight}px`,
                maxHeight: `${tabPanelHeight}px`,
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              <Artwork
                job_id={job_id}
                defaultArtworkFiles={artworkFiles}
                defaultArtworkDescription={artworkDescription}
                defaultArtworkComments={artworkComments}
                status={status}
                setStatus={setStatus}
                setValue={setValue}
                role={role}
                categoryImageFileStep={categoryImageFileStep}
                isSupplierJob={isSupplierJob}
              />
            </TabPanel>
            {categoryImageFileStep == 1 && (
              <TabPanel
                value={value}
                name="ImageFile"
                style={{
                  overflowY: "auto",
                  height: `${tabPanelHeight}px`,
                  maxHeight: `${tabPanelHeight}px`,
                  paddingLeft: "24px",
                  paddingRight: "24px",
                }}
              >
                <Sample
                  job_id={job_id}
                  defaultSampleFiles={sampleFiles}
                  defaultSampleDescription={sampleDescription}
                  status={status}
                  setStatus={setStatus}
                  setValue={setValue}
                  role={role}
                  isSupplierJob={isSupplierJob}
                />
              </TabPanel>
            )}
            <TabPanel
              value={value}
              name="Addon"
              style={{
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
            >
              <Addon
                setStatus={setStatus}
                job_id={job_id}
                jobAddons={addons}
                role={role}
                isSupplierJob={isSupplierJob}
                status={status}
              />
            </TabPanel>
          </Box>
        )}
      </Container>
    </CommonLayout>
  );
};

export default Job;
